/**
 * Paragon Analytics — Pieter Andries
 *
 * Pushes conversion-quality events into window.dataLayer for Google Tag
 * Manager to forward to GA4 and Google Ads. No third-party SDK; just
 * dataLayer pushes. GTM container handles the actual tag firing.
 *
 * Events emitted:
 *   paragon_appointment_booked  — Calendly booking completed (true conversion)
 *   paragon_appointment_open    — Appointment modal/section interaction (intent)
 *   paragon_enquire_open        — PDP Enquire modal opened (intent)
 *   paragon_inquiry_submitted   — Inquiry form posted successfully
 *   paragon_tel_click           — User tapped a tel: link
 *   paragon_email_click         — User tapped a mailto: link
 *
 * Vanilla JS. IIFE. Loaded with `defer` from layout/theme.liquid.
 */
(function () {
  'use strict';

  var dl = (window.dataLayer = window.dataLayer || []);

  function push(event, params) {
    var payload = Object.assign({ event: event }, params || {});
    dl.push(payload);
  }

  var initialized = false;
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  function init() {
    if (initialized) return;
    initialized = true;

    wireCalendlyBookingListener();
    wireInquirySuccessDetection();
    wireDelegatedClicks();
  }

  /* ─── Calendly booking — the real conversion ───
     Calendly's embedded widget emits window.postMessage events. The
     `calendly.event_scheduled` event fires only when the visitor completes
     a booking. This is the high-quality conversion to wire into Google Ads.
  */
  function wireCalendlyBookingListener() {
    window.addEventListener('message', function (e) {
      if (!e || !e.data) return;
      var isCalendly =
        typeof e.data === 'object' &&
        e.data.event &&
        typeof e.data.event === 'string' &&
        e.data.event.indexOf('calendly.') === 0;
      if (!isCalendly) return;

      if (e.data.event === 'calendly.event_scheduled') {
        push('paragon_appointment_booked', {
          appointment_source: locateCalendlySource(),
        });
      }
    });
  }

  function locateCalendlySource() {
    // Differentiates a booking inside the PDP appointment modal from one
    // on a page-level Calendly section (e.g. /pages/visit).
    var modal = document.getElementById('paragon-appointment-modal');
    if (modal && modal.hasAttribute('open')) return 'pdp_modal';
    return 'page_section';
  }

  /* ─── Inquiry form success ───
     Shopify's {% form 'contact' %} round-trips through a POST that reloads
     the page with `form.posted_successfully?` true. The snippet then renders
     a `.paragon-inquiry__status--success` element. We detect it on load and
     fire once per page view.
  */
  function wireInquirySuccessDetection() {
    var success = document.querySelector('.paragon-inquiry__status--success');
    if (!success) return;

    var form = success.closest('.paragon-inquiry');
    var context =
      form && form.classList.contains('paragon-inquiry--modal')
        ? 'pdp_modal'
        : 'inquire_page';

    var productRef = document.querySelector('.paragon-inquiry__product-ref');
    push('paragon_inquiry_submitted', {
      inquiry_source: context,
      inquiry_has_product: productRef ? true : false,
    });
  }

  /* ─── Delegated click handlers ───
     One listener at document level for all click conversions. Cheaper than
     wiring N independent handlers and survives dynamically-injected nodes.
  */
  var APPOINTMENT_HREF_PATTERN = /\/pages\/book-an-appointment|calendly\.com/i;

  function wireDelegatedClicks() {
    document.addEventListener(
      'click',
      function (e) {
        var anchor = closest(e.target, 'a[href]');
        if (anchor) {
          var href = anchor.getAttribute('href') || '';
          if (href.indexOf('tel:') === 0) {
            push('paragon_tel_click', {
              link_text: textOf(anchor),
              link_destination: href.replace(/^tel:/, ''),
              link_location: locationLabel(anchor),
            });
            return;
          }
          if (href.indexOf('mailto:') === 0) {
            push('paragon_email_click', {
              link_text: textOf(anchor),
              link_destination: href.replace(/^mailto:/, ''),
              link_location: locationLabel(anchor),
            });
            return;
          }
          // Any anchor that points at the appointment-booking page or a
          // Calendly URL is treated as an appointment intent.
          if (APPOINTMENT_HREF_PATTERN.test(href)) {
            push('paragon_appointment_open', {
              open_source: locationLabel(anchor),
              link_text: textOf(anchor),
              link_destination: href,
            });
            return;
          }
        }

        // Modal openers — fire intent events
        var modalOpener = closest(e.target, '[data-paragon-open-modal]');
        if (modalOpener) {
          var modalId = modalOpener.getAttribute('data-paragon-open-modal');
          if (modalId === 'paragon-appointment-modal') {
            push('paragon_appointment_open', {
              open_source: 'pdp_sticky_bar',
            });
          } else if (modalId === 'paragon-enquire-modal') {
            push('paragon_enquire_open', {
              open_source: 'pdp_sticky_bar',
              product_handle: productHandleFromDom(),
            });
          }
          return;
        }

        // PDP sticky bar primary action set to "enquire" (some templates fire
        // through this attribute rather than data-paragon-open-modal).
        var primary = closest(e.target, '[data-paragon-primary-action="enquire"]');
        if (primary) {
          push('paragon_enquire_open', {
            open_source: 'pdp_primary_action',
            product_handle: productHandleFromDom(),
          });
        }
      },
      false
    );
  }

  /* ─── Helpers ─── */

  function closest(el, selector) {
    if (!el || el.nodeType !== 1) return null;
    if (typeof el.closest === 'function') return el.closest(selector);
    while (el && el.nodeType === 1) {
      if (el.matches && el.matches(selector)) return el;
      el = el.parentElement;
    }
    return null;
  }

  function textOf(el) {
    var t = (el.textContent || '').replace(/\s+/g, ' ').trim();
    return t.length > 80 ? t.slice(0, 80) : t;
  }

  function locationLabel(el) {
    if (closest(el, 'header, .paragon-header, .paragon-header-minimal')) return 'header';
    if (closest(el, 'footer, .paragon-footer')) return 'footer';
    if (closest(el, '.paragon-product-action-bar')) return 'pdp_sticky_bar';
    if (closest(el, 'dialog.paragon-modal')) return 'modal';
    return 'body';
  }

  function productHandleFromDom() {
    var script = document.getElementById('paragon-product-json');
    if (!script) return null;
    try {
      var product = JSON.parse(script.textContent);
      return product && product.handle ? product.handle : null;
    } catch (err) {
      return null;
    }
  }
})();
